
import { PageContainer } from "@/components/ui/page-container";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GestationTracker } from "@/components/gestation/GestationTracker";
import { GestationForm } from "@/components/gestation-tracking/form/GestationForm";
import { GestationInfo } from "@/components/gestation-tracking/info/GestationInfo";
import { GestationStats } from "@/components/gestation-tracking/stats/GestationStats";
import { BabyDevelopment } from "@/components/gestation-tracking/development/BabyDevelopment";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays } from "date-fns";
import { Plus } from "lucide-react";
import { DailyTipsButton } from "@/components/daily-tips/DailyTipsButton";

const GestationTracking = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pregnancyStartDate, setPregnancyStartDate] = useState<Date | undefined>();

  const { data: gestationData, refetch, isLoading } = useQuery({
    queryKey: ["gestation-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("gestation_tracking")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching gestation data:', error);
        throw error;
      }
      
      return data;
    },
  });

  useEffect(() => {
    if (gestationData?.pregnancy_start_date) {
      const date = new Date(gestationData.pregnancy_start_date);
      if (!isNaN(date.getTime())) {
        setPregnancyStartDate(date);
      }
    }
  }, [gestationData]);

  const dueDate = pregnancyStartDate ? addDays(pregnancyStartDate, 280) : undefined;
  const currentWeek = pregnancyStartDate 
    ? Math.max(0, Math.min(40, Math.floor((new Date().getTime() - pregnancyStartDate.getTime()) / (1000 * 60 * 60 * 24 * 7))))
    : undefined;

  // Données à analyser pour les conseils
  const pregnancyData = pregnancyStartDate && dueDate && currentWeek ? {
    pregnancyStartDate,
    dueDate,
    currentWeek,
    notes: gestationData?.notes,
    nextCheckupDate: gestationData?.next_checkup_date,
    lastCheckupDate: gestationData?.last_checkup_date
  } : undefined;

  return (
    <PageContainer 
      title="Suivi de Grossesse"
      variant="pregnancy"
      intensity="high"
      className="py-6"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            Suivez le développement de votre bébé semaine par semaine et recevez des conseils personnalisés.
          </p>
          <Button 
            onClick={() => setIsFormOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" /> {pregnancyStartDate ? "Modifier" : "Ajouter"}
          </Button>
        </div>

        {isFormOpen && (
          <GestationForm 
            gestationData={gestationData}
            onSuccess={() => {
              refetch();
              setIsFormOpen(false);
            }}
            initialData={{
              pregnancyStartDate,
            }}
          />
        )}

        {/* Bouton de conseils du jour - affiché seulement quand des données existent */}
        {pregnancyData && (
          <div className="flex justify-center my-4">
            <DailyTipsButton 
              dataToAnalyze={pregnancyData} 
              context="pregnancy"
            />
          </div>
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="info">Informations</TabsTrigger>
            <TabsTrigger value="stats">Statistiques</TabsTrigger>
            <TabsTrigger value="development">Développement</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {pregnancyStartDate && dueDate && currentWeek ? (
              <GestationInfo 
                pregnancyStartDate={pregnancyStartDate}
                dueDate={dueDate}
                currentWeek={currentWeek}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Veuillez entrer les informations de votre grossesse pour voir les détails
                </p>
                <Button 
                  onClick={() => setIsFormOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Entrer mes informations
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="info">
            <GestationTracker />
          </TabsContent>

          <TabsContent value="stats">
            {pregnancyStartDate && currentWeek && dueDate ? (
              <GestationStats 
                pregnancyStartDate={pregnancyStartDate}
                currentWeek={currentWeek}
                dueDate={dueDate}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Veuillez entrer les informations de votre grossesse pour voir les statistiques
                </p>
                <Button 
                  onClick={() => setIsFormOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Entrer mes informations
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="development">
            {currentWeek ? (
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-lg border border-primary/20">
                <h3 className="font-semibold mb-4">Développement du bébé</h3>
                <BabyDevelopment 
                  currentWeek={currentWeek}
                />
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Veuillez entrer les informations de votre grossesse pour voir le développement
                </p>
                <Button 
                  onClick={() => setIsFormOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Entrer mes informations
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
};

export default GestationTracking;
